import { useEffect, useState } from 'react';
import { Landmark, Users, KeyRound } from 'lucide-react';
import { EtatJeu, GameData, Maj } from '../game/types';
import { chargerDonnees } from '../game/data';
import { recupererEtatJeu } from '../game/api';
import { chargerEtat, creerEtat, effacerEtat, sauverEtat } from '../game/state';
import { Chrono } from '../components/Chrono';
import Accueil from './Accueil';
import Attente from './Attente';
import Galerie from './Galerie';
import Tatoues from './Tatoues';
import Serrures from './Serrures';
import Fin from './Fin';

type Onglet = 'galerie' | 'tatoues' | 'serrures';

export default function Game() {
  const [data, setData] = useState<GameData | null>(null);
  const [etat, setEtat] = useState<EtatJeu | null>(() => chargerEtat());
  const [onglet, setOnglet] = useState<Onglet>('galerie');
  const [jeuActif, setJeuActif] = useState<boolean>(() => {
    const cache = localStorage.getItem('jeu_actif_cache');
    return cache === null ? true : cache === '1';
  });

  useEffect(() => {
    chargerDonnees().then(setData).catch(() => {});
  }, []);

  useEffect(() => {
    let monte = true;
    const verifier = async () => {
      const v = await recupererEtatJeu();
      if (!monte || v === null) return; // hors-ligne : on garde la dernière valeur connue
      localStorage.setItem('jeu_actif_cache', v ? '1' : '0');
      setJeuActif(v);
    };
    verifier();
    if (!jeuActif) {
      const t = window.setInterval(verifier, 10000);
      return () => {
        monte = false;
        window.clearInterval(t);
      };
    }
    return () => {
      monte = false;
    };
  }, [jeuActif]);

  const maj: Maj = (fn) =>
    setEtat((prev) => {
      if (!prev) return prev;
      const suivant = fn(prev);
      sauverEtat(suivant);
      return suivant;
    });

  if (!data) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="font-script text-4xl text-encre anim-apparition">B &amp; W</p>
      </div>
    );
  }

  if (!etat) {
    if (!jeuActif) return <Attente data={data} />;
    return (
      <Accueil
        data={data}
        onCommencer={(prenom) => {
          const e = creerEtat(prenom, data);
          sauverEtat(e);
          setEtat(e);
        }}
      />
    );
  }

  if (etat.termineLe) {
    return (
      <Fin
        data={data}
        etat={etat}
        maj={maj}
        onReset={() => {
          effacerEtat();
          setEtat(null);
          setOnglet('galerie');
        }}
      />
    );
  }

  const nbIndices = etat.indicesCollectes.length;
  const onglets: { id: Onglet; label: string; badge: string; icone: typeof Landmark }[] = [
    { id: 'galerie', label: 'Galerie', badge: `${etat.questionsOk.length}/5`, icone: Landmark },
    { id: 'tatoues', label: 'Tatoués', badge: `${nbIndices}/10`, icone: Users },
    { id: 'serrures', label: 'Serrures', badge: `${etat.serruresOuvertes.length}/3`, icone: KeyRound },
  ];

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-30 bg-white border-b border-brume">
        <div className="max-w-md mx-auto px-5 h-14 flex items-center justify-between">
          <span className="font-script text-2xl text-encre">B &amp; W</span>
          <Chrono etat={etat} />
          <div className="flex items-center gap-2 max-w-[38%]">
            {etat.penalitesSec > 0 && (
              <span className="text-xs text-erreur font-medium whitespace-nowrap" data-testid="penalites-badge">
                +{Math.round(etat.penalitesSec / 60)} min
              </span>
            )}
            <span className="text-sm text-encre-clair truncate">{etat.prenom}</span>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-5 pt-5 pb-32">
        {onglet === 'galerie' && (
          <Galerie data={data} etat={etat} maj={maj} onAllerTatoues={() => setOnglet('tatoues')} />
        )}
        {onglet === 'tatoues' && <Tatoues data={data} etat={etat} maj={maj} />}
        {onglet === 'serrures' && <Serrures data={data} etat={etat} maj={maj} />}
      </main>

      <nav className="fixed bottom-0 inset-x-0 z-30 bg-white border-t border-brume" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="max-w-md mx-auto flex">
          {onglets.map((o) => {
            const Icone = o.icone;
            const actif = onglet === o.id;
            return (
              <button
                key={o.id}
                data-testid={`tab-${o.id}`}
                onClick={() => setOnglet(o.id)}
                className={`flex-1 min-h-[60px] flex flex-col items-center justify-center gap-0.5 border-t-2 transition-colors duration-200 ${
                  actif ? 'border-encre text-encre' : 'border-transparent text-encre-clair'
                }`}
              >
                <Icone size={20} strokeWidth={actif ? 2.2 : 1.8} />
                <span className="text-[11px] font-medium">
                  {o.label} <span className="tabular-nums">{o.badge}</span>
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
